import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { format } from 'date-fns';

export interface PromotionStatsFCProps {
  strings: {
    orders: string;
    customers: string;
    update: string;
    noOrders: string;
    newCustomers: string;
    totalSpends: string;
    totalRevenue: string;
  };
  orders?: number;
  customers?: number;
  updateDate: number;
  currency?: string;
  providerCurrency?: string;
  newUsers?: number;
  totalSpend?: number;
  totalRevenue?: number;
}

const Stat = ({ string, value }: { string: string; value: ReactNode }) => (
  <Box display="flex" flexDirection="column" minWidth={120} gap={0.5}>
    <Box>
      <Typography variant="bodyXS">{string}</Typography>
    </Box>
    <Box>
      <Typography variant="h5">{value}</Typography>
    </Box>
  </Box>
);
const formatNumber = (value: number | undefined | null) => (!value ? '-' : String(value));

export const PromotionStatsFC = ({
  strings,
  orders,
  customers,
  currency,
  updateDate,
  providerCurrency,
  newUsers,
  totalSpend,
  totalRevenue,
}: PromotionStatsFCProps) => {
  const intl = useIntl();

  const formattedTotalSpends = useMemo(
    () =>
      intl.formatNumber(totalSpend ?? 0, {
        currency: currency || providerCurrency,
        style: 'currency',
      }),
    [totalSpend, currency, intl, providerCurrency],
  );

  const formattedTotalRevenue = useMemo(
    () =>
      intl.formatNumber(totalRevenue ?? 0, {
        currency: currency || providerCurrency,
        style: 'currency',
      }),
    [totalRevenue, currency, intl, providerCurrency],
  );

  const lastOrderInfo = useMemo(() => {
    if (!orders) {
      return strings.noOrders;
    }

    const formattedUpdateDate = format(new Date(updateDate * 1000), 'MMM dd, yyyy, HH:mm');
    return `${strings.update} ${formattedUpdateDate}`;
  }, [updateDate, strings, orders]);

  return (
    <Box paddingBottom={4}>
      <Grid container marginLeft="2px" direction="column" gap={1.5}>
        <Grid container xs={12} direction="row" marginBottom={2}>
          <Grid item xs={4}>
            <Stat string={strings.totalSpends} value={formattedTotalSpends} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.orders} value={formatNumber(orders)} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.totalRevenue} value={formattedTotalRevenue} />
          </Grid>
        </Grid>
        <Grid container xs={12} direction="row" marginBottom={2}>
          <Grid item xs={4}>
            <Stat string={strings.newCustomers} value={formatNumber(newUsers)} />
          </Grid>
          <Grid item xs={4}>
            <Stat string={strings.customers} value={formatNumber(customers)} />
          </Grid>
        </Grid>
      </Grid>
      <Typography variant="caption" fontSize="14px">
        {lastOrderInfo}
      </Typography>
    </Box>
  );
};
